import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createEvent,
  clearEventState,
  fetchEventsList,
  updateEvent,
  deleteEvent,
} from "../redux/actions/eventsActions";
import Toast from "../common/toast-message/Toast";
import useToast from "../common/toast-message/useToast";

const Events = () => {
  const dispatch = useDispatch();
  const { adminData } = useSelector((state) => state.admin);
  const { loading, error, success, eventsList } = useSelector(
    (state) => state.events
  );
  const [currentTab, setCurrentTab] = useState("upcoming");
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [deletingEventId, setDeletingEventId] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);

  const [eventData, setEventData] = useState({
    eventTitle: "",
    eventDate: "",
    eventTime: "",
    eventLink: "",
    eventPic: "",
    eventMode: "online",
    eventLocation: "",
    aboutEvent: "",
  });

  const { toastMessage, showToastMessage, hideToast } = useToast();

  // Format date for input (yyyy-mm-dd) from display (dd-mm-yyyy)
  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;

    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const [dd, mm, yyyy] = dateStr.split("-");
      return `${yyyy}-${mm}-${dd}`;
    }
    return dateStr;
  };

  // Format date for display (dd-mm-yyyy) from input (yyyy-mm-dd)
  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return "";
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) return dateStr;

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [yyyy, mm, dd] = dateStr.split("-");
      return `${dd}-${mm}-${yyyy}`;
    }
    return dateStr;
  };

  // Get today's date in yyyy-mm-dd format
  const getTodayDate = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // Extract event ID from PK
  const getEventIdFromPK = (PK) => {
    if (!PK) return "";
    return PK.includes("#") ? PK.split("#")[1] : PK;
  };

  // Categorize events as upcoming or previous
  const getCategorizedEvents = () => {
    const eventsListData = eventsList || {};
    if (eventsListData.upcomingEvents && eventsListData.previousEvents) {
      return {
        upcomingEvents: eventsListData.upcomingEvents,
        previousEvents: eventsListData.previousEvents,
      };
    }
    const allEvents = eventsListData.allEvents || [];
    const upcomingEvents = eventsListData.upcomingEvents || [];
    const previousEvents = eventsListData.previousEvents || [];

    if (upcomingEvents.length > 0 || previousEvents.length > 0) {
      return { upcomingEvents, previousEvents };
    }

    const currentDate = new Date().toISOString().split("T")[0];

    const categorized = {
      upcomingEvents: [],
      previousEvents: [],
    };

    allEvents.forEach((event) => {
      const eventDate = event.eventDate;
      if (eventDate) {
        let compareDate = eventDate;
        if (/^\d{2}-\d{2}-\d{4}$/.test(eventDate)) {
          const [dd, mm, yyyy] = eventDate.split("-");
          compareDate = `${yyyy}-${mm}-${dd}`;
        }

        if (compareDate >= currentDate) {
          categorized.upcomingEvents.push(event);
        } else {
          categorized.previousEvents.push(event);
        }
      }
    });

    categorized.upcomingEvents.sort((a, b) => {
      const dateA = a.eventDate ? formatDateForInput(a.eventDate) : "";
      const dateB = b.eventDate ? formatDateForInput(b.eventDate) : "";
      return dateA.localeCompare(dateB);
    });

    categorized.previousEvents.sort((a, b) => {
      const dateA = a.eventDate ? formatDateForInput(a.eventDate) : "";
      const dateB = b.eventDate ? formatDateForInput(b.eventDate) : "";
      return dateB.localeCompare(dateA);
    });

    return categorized;
  };

  const categorizedEvents = getCategorizedEvents();
  const eventsToShow =
    currentTab === "upcoming"
      ? categorizedEvents.upcomingEvents
      : categorizedEvents.previousEvents;

  useEffect(() => {
    if (adminData) {
      dispatch(fetchEventsList(adminData));
    }
  }, [adminData, dispatch]);

  useEffect(() => {
    if (success) {
      if (showCreatePopup) {
        resetForm();
        setShowCreatePopup(false);
        showToastMessage("Event created successfully!");
        setTimeout(() => {
          dispatch(clearEventState());
        }, 1000);
      }
      if (showDeletePopup) {
        setShowDeletePopup(false);
        setDeletingEventId(null);
        showToastMessage("Event deleted successfully!");
        setTimeout(() => {
          dispatch(clearEventState());
        }, 1000);
      }
      if (showEditPopup) {
        setShowEditPopup(false);
        setEditingEvent(null);
        showToastMessage("Event updated successfully!");
        setTimeout(() => {
          dispatch(clearEventState());
        }, 1000);
      }
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      showToastMessage(error);
    }
  }, [error]);

  useEffect(() => {
    console.log("Events list from Redux:", eventsList);
    console.log("Categorized events:", categorizedEvents);
    console.log("Events to show:", eventsToShow.length);
  }, [eventsList, categorizedEvents, eventsToShow]);

  const resetForm = () => {
    setEventData({
      eventTitle: "",
      eventDate: "",
      eventTime: "",
      eventLink: "",
      eventPic: "",
      eventMode: "online",
      eventLocation: "",
      aboutEvent: "",
    });
    setImagePreview("");
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setEventData({ ...eventData, eventPic: reader.result });
      };
    }
  };

  const isFormDisabled = () => {
    return (
      !eventData.eventTitle ||
      !eventData.eventDate ||
      !eventData.eventTime ||
      !eventData.eventLink ||
      !eventData.eventPic ||
      !eventData.aboutEvent ||
      !eventData.eventLocation ||
      eventData.eventLocation.trim() === ""
    );
  };

  const handleCreateEvent = () => {
    if (adminData && !isFormDisabled()) {
      const base64String = eventData.eventPic?.includes(",")
        ? eventData.eventPic.split(",")[1]
        : eventData.eventPic;

      let formattedDate = formatDateForDisplay(eventData.eventDate);

      const payload = {
        id: adminData.id,
        eventTitle: eventData.eventTitle,
        eventDate: formattedDate,
        eventTime: eventData.eventTime,
        eventLink: eventData.eventLink,
        eventPic: base64String,
        eventMode: eventData.eventMode,
        eventLocation: eventData.eventLocation,
        aboutEvent: eventData.aboutEvent,
      };
      // Show loading
      setLocalLoading(true);

      dispatch(createEvent(adminData, payload))
        .then(() => {
          return dispatch(fetchEventsList(adminData));
        })
        .then(() => {
          setLocalLoading(false);
        })
        .catch((error) => {
          setLocalLoading(false);
          showToastMessage(
            "Failed to create event: " + (error?.message || "Unknown error")
          );
        });
    }
  };

  const openEditPopup = (event) => {
    setEditingEvent(event);
    setEventData({
      eventTitle: event.eventTitle || "",
      eventDate: formatDateForInput(event.eventDate || ""),
      eventTime: event.eventTime || "",
      eventLink: event.eventLink || "",
      eventPic: event.eventPic || "",
      eventMode: event.eventMode || "online",
      eventLocation: event.eventLocation || "",
      aboutEvent: event.aboutEvent || "",
    });

    if (event.eventPic) {
      setImagePreview(`${event.eventPic}?t=${Date.now()}`);
    } else {
      setImagePreview("");
    }

    setShowEditPopup(true);
  };

  const handleUpdateEvent = () => {
    if (!editingEvent) return;

    const eventId = getEventIdFromPK(editingEvent.PK);

    let eventPicValue = eventData.eventPic;
    let base64String = "";

    if (eventPicValue && eventPicValue.includes(",")) {
      base64String = eventPicValue.split(",")[1];
    } else if (eventPicValue && !eventPicValue.includes(",")) {
      base64String = "";
    }

    let formattedDate = formatDateForDisplay(eventData.eventDate);

    const payload = {
      id: adminData.id,
      eventId: eventId,
      eventTitle: eventData.eventTitle,
      eventDate: formattedDate,
      eventTime: eventData.eventTime,
      eventLink: eventData.eventLink,
      eventMode: eventData.eventMode,
      eventLocation: eventData.eventLocation,
      aboutEvent: eventData.aboutEvent,
      ...(base64String
        ? { eventPic: base64String }
        : { existingEventPicUrl: eventData.eventPic }),
    };

    dispatch(updateEvent(adminData, payload))
      .then(() => {
        dispatch(fetchEventsList(adminData));
      })
      .catch((error) => {
        showToastMessage(
          "Failed to update event: " + (error?.message || "Unknown error")
        );
      });
  };

  const openDeletePopup = (event) => {
    const id = getEventIdFromPK(event.PK);
    setDeletingEventId(id);
    setShowDeletePopup(true);
  };

  const handleDeleteEvent = () => {
    if (!deletingEventId) return;

    console.log("Deleting event with ID:", deletingEventId); // Add this for debugging

    const payload = {
      id: adminData.id,
      eventId: deletingEventId, // Make sure this is just the UUID, not the full PK
    };

    dispatch(deleteEvent(adminData, payload))
      .then(() => {
        dispatch(fetchEventsList(adminData));
      })
      .catch((error) => {
        showToastMessage(
          "Failed to delete event: " + (error?.message || "Unknown error")
        );
      });
  };

  const handleCloseEditPopup = () => {
    setShowEditPopup(false);
    setEditingEvent(null);
    resetForm();
  };

  // Function to handle event link click
  const handleEventLinkClick = (link) => {
    if (link) {
      // Ensure the link has proper protocol
      const formattedLink = link.startsWith('http') ? link : `https://${link}`;
      window.open(formattedLink, '_blank', 'noopener,noreferrer');
    }
  };

  // Get appropriate location icon based on event mode
  const getLocationIcon = (eventMode) => {
    return eventMode === "online" ? (
      <svg
        className="w-4 h-4 mr-2 text-blue-500 flex-shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3"
        />
      </svg>
    ) : (
      <svg
        className="w-4 h-4 mr-2 text-green-500 flex-shrink-0"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    );
  };

  // Get location text based on event mode
  const getLocationText = (event) => {
    if (!event.eventLocation || event.eventLocation.trim() === "") {
      return event.eventMode === "online" 
        ? "Online platform not specified" 
        : "Venue address not specified";
    }
    return event.eventLocation;
  };

  if (loading && !eventsToShow.length) {
    return (
      <div className="flex items-center justify-center min-h-screen w-full">
        <svg
          className="animate-spin h-12 w-12 text-gray-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Events Management</h1>
        <div className="flex items-center">
          <button
            className={`mr-4 px-3 py-2 ${
              currentTab === "upcoming"
                ? "font-bold text-blue-600 border-b-2 border-blue-600"
                : "text-gray-600"
            }`}
            onClick={() => setCurrentTab("upcoming")}
          >
            Upcoming ({categorizedEvents.upcomingEvents.length})
          </button>
          <button
            className={`mr-4 px-3 py-2 ${
              currentTab === "previous"
                ? "font-bold text-blue-600 border-b-2 border-blue-600"
                : "text-gray-600"
            }`}
            onClick={() => setCurrentTab("previous")}
          >
            Previous ({categorizedEvents.previousEvents.length})
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 text-sm"
            onClick={() => {
              resetForm(); // ADD THIS LINE - Reset form before opening modal
              setShowCreatePopup(true);
            }}
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create Event
          </button>
        </div>
      </div>

      {/* EVENT CARDS SECTION - UPDATED FOR FULL IMAGES */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {eventsToShow.length > 0 ? (
          eventsToShow.map((event, index) => (
            <div
              key={event.PK || `event-${index}`}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow flex flex-col"
            >
              {/* Image Container - Updated to show full image */}
              <div className="relative bg-gray-100 min-h-[200px] flex items-center justify-center overflow-hidden">
                {event.eventPic ? (
                  <img
                    src={`${event.eventPic}?t=${Date.now()}`}
                    alt={event.eventTitle}
                    className="w-full h-auto max-h-[300px] object-contain"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src =
                        "https://via.placeholder.com/400x200?text=No+Image";
                    }}
                  />
                ) : (
                  <div className="w-full h-[200px] flex items-center justify-center bg-gray-200">
                    <span className="text-gray-500">No Image</span>
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <button
                    onClick={() => openEditPopup(event)}
                    className="bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 shadow"
                    title="Edit"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={() => openDeletePopup(event)}
                    className="bg-red-600 text-white p-2 rounded-full hover:bg-red-700 shadow"
                    title="Delete"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                </div>
                <div className="absolute bottom-2 left-2">
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      currentTab === "upcoming"
                        ? "bg-green-100 text-green-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {currentTab === "upcoming" ? "Upcoming" : "Past"}
                  </span>
                </div>
              </div>

              {/* Content Section */}
              <div className="p-4 flex-grow">
                <h3 className="font-bold text-lg mb-2 truncate">
                  {event.eventTitle}
                </h3>
                <div className="text-sm text-gray-600 mb-2 line-clamp-2">
                  {event.aboutEvent}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    <span>{formatDateForDisplay(event.eventDate)}</span>
                  </div>
                  <div className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{event.eventTime}</span>
                  </div>
                  <div className="flex items-center">
                    {event.eventMode === "online" ? (
                      <>
                        <svg
                          className="w-4 h-4 mr-2 text-blue-500 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3"
                          />
                        </svg>
                        <span className="font-medium">Online Event</span>
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4 mr-2 text-green-500 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        <span className="font-medium">Offline Event</span>
                      </>
                    )}
                  </div>
                  {/* Location details for ALL events */}
                  <div className="flex items-start">
                    {getLocationIcon(event.eventMode)}
                    <span className="text-gray-600">
                      {getLocationText(event)}
                      {(!event.eventLocation || event.eventLocation.trim() === "") && (
                        <span className="text-red-500 text-xs ml-1">(Required)</span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-2 text-gray-400 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                      />
                    </svg>
                    <button
                      onClick={() => handleEventLinkClick(event.eventLink)}
                      className="text-blue-600 hover:text-blue-800 hover:underline truncate text-left"
                      title={event.eventLink}
                    >
                      {event.eventLink.length > 35
                        ? `${event.eventLink.substring(0, 35)}...`
                        : event.eventLink}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-3 text-center py-12">
            <svg
              className="w-16 h-16 mx-auto text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              No events found
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {currentTab === "upcoming"
                ? "Get started by creating a new upcoming event."
                : "No past events available."}
            </p>
            {currentTab === "upcoming" && (
              <button
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={() => setShowCreatePopup(true)}
              >
                Create Event
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create Event Modal */}
      {showCreatePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center border-b pb-4 mb-4">
              <h2 className="text-xl font-bold">Create New Event</h2>
              <button
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                onClick={() => {
                  setShowCreatePopup(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>

            <div className="text-center mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Event Picture *
              </label>
              <div className="w-40 h-40 bg-gray-50 rounded-lg mx-auto cursor-pointer relative border-2 border-dashed border-gray-300 hover:border-blue-400 transition duration-200 flex items-center justify-center">
                <input
                  type="file"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
                {eventData.eventPic ? (
                  <img
                    src={eventData.eventPic}
                    alt="Event Cover"
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <div className="text-center p-4">
                    <div className="text-gray-400 mb-1">
                      <svg
                        className="w-8 h-8 mx-auto"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <span className="text-xs text-gray-500 block">
                      Click to upload
                    </span>
                    <span className="text-xs text-gray-400">
                      PNG, JPG, JPEG
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Title *
                </label>
                <input
                  type="text"
                  placeholder="Enter event title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={eventData.eventTitle}
                  onChange={(e) =>
                    setEventData({ ...eventData, eventTitle: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Date *
                </label>
                <input
                  type="date"
                  min={getTodayDate()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={eventData.eventDate}
                  onChange={(e) =>
                    setEventData({ ...eventData, eventDate: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Time *
                </label>
                <input
                  type="time"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={eventData.eventTime}
                  onChange={(e) =>
                    setEventData({ ...eventData, eventTime: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Mode *
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={eventData.eventMode}
                  onChange={(e) =>
                    setEventData({ ...eventData, eventMode: e.target.value })
                  }
                >
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Link *
                </label>
                <input
                  type="url"
                  placeholder="https://example.com/event"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={eventData.eventLink}
                  onChange={(e) =>
                    setEventData({ ...eventData, eventLink: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Location *
                </label>
                <textarea
                  placeholder={
                    eventData.eventMode === "online"
                      ? "Enter virtual event platform/link (e.g., Zoom, Google Meet, etc.)"
                      : "Enter the complete event venue address"
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg h-24 resize-none"
                  value={eventData.eventLocation}
                  onChange={(e) =>
                    setEventData({
                      ...eventData,
                      eventLocation: e.target.value,
                    })
                  }
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {eventData.eventMode === "online" 
                    ? "Required: Provide the online platform or link for the virtual event"
                    : "Required: Provide the physical venue address"}
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  About Event *
                </label>
                <textarea
                  placeholder="Describe the event details, agenda, speakers, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg h-32 resize-none"
                  value={eventData.aboutEvent}
                  onChange={(e) =>
                    setEventData({ ...eventData, aboutEvent: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                className={`px-6 py-2 rounded-lg font-medium transition duration-200 ${
                  isFormDisabled() || loading
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
                }`}
                onClick={handleCreateEvent}
                disabled={isFormDisabled() || loading}
              >
                {loading ? "Creating..." : "Create Event"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center border-b pb-4 mb-4">
              <h2 className="text-xl font-bold">Edit Event</h2>
              <button
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                onClick={handleCloseEditPopup}
              >
                ×
              </button>
            </div>

            <div className="text-center mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Event Picture *
              </label>
              <div className="w-40 h-40 bg-gray-50 rounded-lg mx-auto cursor-pointer relative border-2 border-dashed border-gray-300 hover:border-blue-400 transition duration-200 flex items-center justify-center">
                <input
                  type="file"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept="image/*"
                  onChange={handleImageUpload}
                />
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Event Cover"
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <div className="text-center p-4">
                    <div className="text-gray-400 mb-1">
                      <svg
                        className="w-8 h-8 mx-auto"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <span className="text-xs text-gray-500 block">
                      Click to upload new image
                    </span>
                    <span className="text-xs text-gray-400">
                      PNG, JPG, JPEG
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Title *
                </label>
                <input
                  type="text"
                  placeholder="Enter event title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={eventData.eventTitle}
                  onChange={(e) =>
                    setEventData({ ...eventData, eventTitle: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Date *
                </label>
                <input
                  type="date"
                  min={getTodayDate()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={eventData.eventDate}
                  onChange={(e) =>
                    setEventData({ ...eventData, eventDate: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Time *
                </label>
                <input
                  type="time"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={eventData.eventTime}
                  onChange={(e) =>
                    setEventData({ ...eventData, eventTime: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Mode *
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={eventData.eventMode}
                  onChange={(e) =>
                    setEventData({ ...eventData, eventMode: e.target.value })
                  }
                >
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Link *
                </label>
                <input
                  type="url"
                  placeholder="https://example.com/event"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  value={eventData.eventLink}
                  onChange={(e) =>
                    setEventData({ ...eventData, eventLink: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Location *
                </label>
                <textarea
                  placeholder={
                    eventData.eventMode === "online"
                      ? "Enter virtual event platform/link (e.g., Zoom, Google Meet, etc.)"
                      : "Enter the complete event venue address"
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg h-24 resize-none"
                  value={eventData.eventLocation}
                  onChange={(e) =>
                    setEventData({
                      ...eventData,
                      eventLocation: e.target.value,
                    })
                  }
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {eventData.eventMode === "online" 
                    ? "Required: Provide the online platform or link for the virtual event"
                    : "Required: Provide the physical venue address"}
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  About Event *
                </label>
                <textarea
                  placeholder="Describe the event details, agenda, speakers, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg h-32 resize-none"
                  value={eventData.aboutEvent}
                  onChange={(e) =>
                    setEventData({ ...eventData, aboutEvent: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleCloseEditPopup}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateEvent}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                disabled={
                  !eventData.eventTitle ||
                  !eventData.eventDate ||
                  !eventData.eventTime ||
                  !eventData.eventLocation ||
                  eventData.eventLocation.trim() === ""
                }
              >
                Update Event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeletePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm">
            <div className="text-center mb-4">
              <svg
                className="w-12 h-12 mx-auto text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.282 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <h3 className="mt-2 text-lg font-semibold text-gray-900">
                Delete Event
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete this event?
              </p>
            </div>
            <div className="flex justify-center gap-3 mt-6">
              <button
                onClick={() => setShowDeletePopup(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteEvent}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toastMessage} onClose={hideToast} />
    </div>
  );
};

export default Events;